import { type FC, useState, useEffect, useCallback } from 'react'
import { X, Link2, Send, Check, Zap, Copy, Users, ArrowRight, Sparkles } from 'lucide-react'
import { Modal } from '@/components/ds2'
import { Badge, Spinner } from '../components/billingControls'
import { getReferralLink, sendReferralInvite, listReferrals, type Referral } from './referrals.api'

interface ReferralModalProps {
    open: boolean
    onClose: () => void
}

export const ReferralModal: FC<ReferralModalProps> = ({ open, onClose }) => {
    const [tab, setTab] = useState<'refer' | 'past'>('refer')
    const [referralLink, setReferralLink] = useState('')
    const [loadingLink, setLoadingLink] = useState(false)
    const [copied, setCopied] = useState(false)
    const [email, setEmail] = useState('')
    const [sending, setSending] = useState(false)
    const [sendSuccess, setSendSuccess] = useState(false)
    const [sendError, setSendError] = useState<string | null>(null)
    const [referrals, setReferrals] = useState<Referral[]>([])
    const [loadingReferrals, setLoadingReferrals] = useState(false)
    const [linkError, setLinkError] = useState(false)
    const [referralsError, setReferralsError] = useState(false)

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

    const fetchLink = useCallback(async () => {
        setLoadingLink(true)
        setLinkError(false)
        try {
            const res = await getReferralLink()
            if (res.success && res.data) setReferralLink(res.data.link)
        } catch {
            setLinkError(true)
        }
        setLoadingLink(false)
    }, [])

    const fetchReferrals = useCallback(async () => {
        setLoadingReferrals(true)
        setReferralsError(false)
        try {
            const res = await listReferrals()
            if (res.success && res.data) setReferrals(res.data)
        } catch {
            setReferralsError(true)
        }
        setLoadingReferrals(false)
    }, [])

    useEffect(() => {
        if (open) {
            fetchLink()
            fetchReferrals()
            setSendSuccess(false)
            setSendError(null)
            setEmail('')
        }
    }, [open, fetchLink, fetchReferrals])

    const handleCopy = () => {
        if (referralLink) {
            navigator.clipboard.writeText(referralLink)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const handleSend = async () => {
        if (!isValidEmail) return
        setSending(true)
        setSendError(null)
        setSendSuccess(false)
        try {
            const res = await sendReferralInvite({ refereeEmail: email.trim() })
            if (res.success) {
                setSendSuccess(true)
                setEmail('')
                fetchReferrals()
            } else setSendError(res.message || 'Failed to send invite')
        } catch {
            setSendError('Failed to send invite. Please try again.')
        }
        setSending(false)
    }

    if (!open) return null

    const earnedCount = referrals.filter((r) => r.rewardGranted).length

    return (
        <Modal open onClose={onClose} size="sm" showCloseButton={false} disableTransform style={{ width: 'min(500px, calc(100vw - 32px))' }}>
            <div className="-mx-6 -mb-6 -mt-6 flex flex-col">
                {/* ── Header ── */}
                <div className="relative overflow-hidden bg-accent px-6 pb-7 pt-6">
                    {/* Decorative circles */}
                    <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-accent-foreground/10" />
                    <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-accent-foreground/10" />
                    <div className="absolute right-16 top-2 h-8 w-8 rounded-full bg-accent-foreground/10" />

                    <div className="relative flex items-start justify-between">
                        <div className="flex-1">
                            <div className="mb-1.5 flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-accent-foreground/70" />
                                <span className="text-[11px] font-semibold uppercase tracking-wider text-accent-foreground/70">Referral Program</span>
                            </div>
                            <h2 style={{ fontSize: 22, fontWeight: 700 }} className="leading-tight text-accent-foreground">
                                Get free months of Pro
                            </h2>
                            <p className="mt-1.5 text-sm leading-relaxed text-accent-foreground/70">
                                Refer friends and earn <strong className="text-accent-foreground">1 month free</strong> for each signup.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="ml-3 shrink-0 rounded-lg p-1 text-accent-foreground/70 transition-colors hover:bg-accent-foreground/10 hover:text-accent-foreground"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Stats row */}
                    <div className="relative mt-5 flex gap-3">
                        <div className="flex flex-1 items-center gap-2.5 rounded-xl bg-accent-foreground/10 px-3.5 py-2.5 backdrop-blur-sm">
                            <Users className="h-4 w-4 text-accent-foreground/70" />
                            <div>
                                <p className="text-lg font-bold leading-none text-accent-foreground">{referrals.length}</p>
                                <p className="mt-0.5 text-[10px] text-accent-foreground/70">Referred</p>
                            </div>
                        </div>
                        <div className="flex flex-1 items-center gap-2.5 rounded-xl bg-accent-foreground/10 px-3.5 py-2.5 backdrop-blur-sm">
                            <Zap className="h-4 w-4 text-accent-foreground/70" />
                            <div>
                                <p className="text-lg font-bold leading-none text-accent-foreground">{earnedCount * 30}d</p>
                                <p className="mt-0.5 text-[10px] text-accent-foreground/70">Earned</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Tabs ── */}
                <div className="px-6">
                    <div className="flex gap-1 border-b border-border">
                        {(['refer', 'past'] as const).map((t) => (
                            <button
                                key={t}
                                onClick={() => setTab(t)}
                                className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${tab === t ? 'text-accent' : 'text-foreground-muted hover:text-foreground'}`}
                            >
                                {t === 'refer' ? 'Invite' : `History (${referrals.length})`}
                                {tab === t && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent" />}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Content ── */}
                <div className="px-6 py-5">
                    {tab === 'refer' && (
                        <div className="flex flex-col gap-5">
                            {/* How it works */}
                            <div className="flex flex-col gap-2.5">
                                {[
                                    {
                                        icon: <Link2 className="h-4 w-4" />,
                                        color: 'bg-accent-soft text-accent-soft-foreground',
                                        text: 'Share your unique invite link',
                                    },
                                    {
                                        icon: <Users className="h-4 w-4" />,
                                        color: 'bg-success-soft text-success-soft-foreground',
                                        text: 'They sign up and get a free month of Pro',
                                    },
                                    {
                                        icon: <Zap className="h-4 w-4" />,
                                        color: 'bg-warning-soft text-warning-soft-foreground',
                                        text: 'You earn 30 days free when they connect email',
                                    },
                                ].map((step, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${step.color}`}>{step.icon}</div>
                                        <span className="text-[13px] text-foreground">{step.text}</span>
                                        {i < 2 && <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0 text-foreground-muted" />}
                                    </div>
                                ))}
                            </div>

                            {/* Invite link */}
                            <div>
                                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                                    Your invite link
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex h-9 flex-1 items-center overflow-hidden rounded-lg border border-border bg-surface-secondary px-3">
                                        {loadingLink ? (
                                            <Spinner size="sm" />
                                        ) : linkError ? (
                                            <button onClick={fetchLink} className="text-xs text-danger hover:text-danger">
                                                Failed — retry
                                            </button>
                                        ) : (
                                            <span className="truncate font-mono text-xs text-foreground-muted">{referralLink}</span>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleCopy}
                                        disabled={!referralLink}
                                        className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-all disabled:opacity-50 ${copied ? 'border-success bg-success-soft text-success-soft-foreground' : 'border-border text-foreground-muted hover:bg-default'}`}
                                    >
                                        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                        {copied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>

                            {/* Send invite */}
                            <div>
                                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                                    Send invite via email
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        placeholder="friend@example.com"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value)
                                            setSendSuccess(false)
                                            setSendError(null)
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && isValidEmail && handleSend()}
                                        className="h-9 flex-1 rounded-xl border border-transparent bg-default px-3 text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                    />
                                    <button
                                        onClick={handleSend}
                                        disabled={!isValidEmail || sending}
                                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-accent px-4 text-xs font-semibold text-accent-foreground transition-colors hover:bg-accent-hover disabled:opacity-50"
                                    >
                                        {sending ? <Spinner size="sm" className="!text-accent-foreground" /> : <Send className="h-3.5 w-3.5" />}
                                        Send
                                    </button>
                                </div>
                                {sendSuccess && (
                                    <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-success">
                                        <Check className="h-3 w-3" /> Invite sent!
                                    </p>
                                )}
                                {sendError && <p className="mt-1.5 text-xs text-danger">{sendError}</p>}
                            </div>
                        </div>
                    )}

                    {tab === 'past' && (
                        <div>
                            {loadingReferrals ? (
                                <div className="flex justify-center py-10">
                                    <Spinner />
                                </div>
                            ) : referralsError ? (
                                <div className="py-10 text-center">
                                    <p className="mb-2 text-sm text-danger">Failed to load referrals.</p>
                                    <button onClick={fetchReferrals} className="text-sm font-medium text-accent hover:text-accent">
                                        Retry
                                    </button>
                                </div>
                            ) : referrals.length === 0 ? (
                                <div className="flex flex-col items-center py-10 text-center">
                                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-default">
                                        <Users className="h-5 w-5 text-foreground-muted" />
                                    </div>
                                    <p className="text-sm font-medium text-foreground-muted">No referrals yet</p>
                                    <p className="mt-0.5 text-xs text-foreground-muted">Share your link to get started</p>
                                </div>
                            ) : (
                                <div className="max-h-64 space-y-1.5 overflow-y-auto">
                                    {referrals.map((r) => (
                                        <div key={r.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                                            <span className="truncate text-sm text-foreground">{r.refereeEmail}</span>
                                            <Badge
                                                variant="soft"
                                                type={r.rewardGranted ? 'success' : r.status === 'signed_up' ? 'warning' : 'default'}
                                                size="sm"
                                            >
                                                {r.rewardGranted ? '+30 days' : r.status === 'signed_up' ? 'Awaiting email' : 'Invited'}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="border-t border-border px-6 py-3 text-center">
                    <p className="text-[11px] text-foreground-muted">Rewards applied when your referral connects their email.</p>
                </div>
            </div>
        </Modal>
    )
}
